import { useState, useEffect, useCallback, useRef } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiClient } from '@/lib/api';
import { Lesson, LessonStatus, Section, Assignment } from '@/lib/types';
import LessonBlockEditor from './LessonBlockEditor';

interface LessonManagerProps {
    courseId: number;
    sectionId: number;
    sectionTitle: string;
    sections?: Section[];
    onLessonsChange?: () => void;
}

const STATUS_BADGE: Record<LessonStatus, string> = {
    draft: 'bg-amber-100 text-amber-700',
    published: 'bg-emerald-100 text-emerald-700',
};

function SortableLesson({
    lesson,
    isSelected,
    onSelect,
    onDelete,
}: {
    lesson: Lesson;
    isSelected: boolean;
    onSelect: () => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
            onClick={onSelect}
            className={`rounded-xl border bg-white p-3.5 cursor-pointer transition-colors ${
                isSelected ? 'border-blue-500 shadow-sm' : 'border-slate-200 hover:border-slate-300'
            }`}
        >
            <div className="flex items-center gap-2">
                <button
                    {...attributes}
                    {...listeners}
                    onClick={e => e.stopPropagation()}
                    className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 p-1 rounded"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{lesson.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[lesson.status]}`}>
                            {lesson.status}
                        </span>
                        {lesson.duration_minutes && (
                            <span className="text-[10px] text-slate-400">{lesson.duration_minutes}min</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={e => { e.stopPropagation(); onDelete(); }}
                    className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                >
                    Del
                </button>
            </div>
        </div>
    );
}

export default function LessonManager({ courseId, sectionId, sectionTitle, sections = [], onLessonsChange }: LessonManagerProps) {
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [showAddAssignment, setShowAddAssignment] = useState(false);
    const [newAssignmentTitle, setNewAssignmentTitle] = useState('');
    const [newAssignmentDescription, setNewAssignmentDescription] = useState('');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const loadLessons = useCallback(async (opts?: { quiet?: boolean }) => {
        if (!opts?.quiet) setLoading(true);
        const res = await apiClient.getLessonsBySection(sectionId);
        if (res.success && res.data) {
            const items = Array.isArray(res.data) ? res.data : (res.data as any)?.results || [];
            const sorted = items.filter((l: Lesson) => l.section === sectionId).sort((a: Lesson, b: Lesson) => a.order - b.order);
            setLessons(sorted);
            // Quiet refresh keeps the editor mounted; only drop selection if the lesson vanished.
            setSelectedLesson(prev => {
                if (!prev) return null;
                const updated = sorted.find((l: Lesson) => l.id === prev.id);
                return updated ? (opts?.quiet ? prev : updated) : null;
            });
        } else {
            setError('Failed to load lessons');
        }
        if (!opts?.quiet) setLoading(false);
    }, [sectionId]);

    const loadAssignments = useCallback(async () => {
        const res = await apiClient.getAssignments(courseId, sectionId);
        if (res.success && res.data) {
            const items = (res.data as { results?: Assignment[] }).results ?? res.data;
            setAssignments(Array.isArray(items) ? items : []);
        }
    }, [courseId, sectionId]);

    useEffect(() => {
        setSelectedLesson(null);
        setShowAddAssignment(false);
        loadLessons();
        loadAssignments();
    }, [sectionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = lessons.findIndex(l => l.id === active.id);
        const newIndex = lessons.findIndex(l => l.id === over.id);
        const reordered = arrayMove(lessons, oldIndex, newIndex);
        setLessons(reordered);
        setReordering(true);

        const res = await apiClient.reorderLessons(reordered.map(l => l.id));
        setReordering(false);
        if (!res.success) {
            setError('Failed to save order. Reloading…');
            loadLessons();
        } else {
            onLessonsChange?.();
        }
    };

    const handleAddLesson = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTitle.trim()) return;
        const res = await apiClient.createLesson({
            section: sectionId,
            title: newTitle.trim(),
            order: lessons.length + 1,
        });
        if (res.success) {
            setNewTitle('');
            setShowAddForm(false);
            await loadLessons();
            onLessonsChange?.();
        } else {
            setError('Failed to create lesson');
        }
    };

    const handleAddAssignment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newAssignmentTitle.trim()) return;
        const res = await apiClient.createAssignment({
            course: courseId,
            section: sectionId,
            title: newAssignmentTitle.trim(),
            description: newAssignmentDescription.trim(),
        });
        if (res.success) {
            setNewAssignmentTitle('');
            setNewAssignmentDescription('');
            setShowAddAssignment(false);
            await loadAssignments();
        } else {
            setError(res.error || 'Failed to create assignment');
        }
    };

    const handleDeleteAssignment = async (assignment: Assignment) => {
        if (!confirm(`Delete assignment "${assignment.title}"?`)) return;
        const res = await apiClient.deleteAssignment(assignment.id);
        if (res.success) {
            await loadAssignments();
        } else {
            setError(res.error || 'Failed to delete assignment');
        }
    };

    const handleDeleteLesson = async (lesson: Lesson) => {
        if (!confirm(`Delete lesson "${lesson.title}"?`)) return;
        const res = await apiClient.deleteLesson(lesson.id);
        if (res.success) {
            if (selectedLesson?.id === lesson.id) setSelectedLesson(null);
            await loadLessons();
            onLessonsChange?.();
        } else {
            setError('Failed to delete lesson');
        }
    };

    const handleLessonUpdated = (fields?: Partial<Lesson>) => {
        if (!fields) return;
        const id = fields.id ?? selectedLesson?.id;
        if (id == null) return;
        setLessons(prev => prev.map(l => (l.id === id ? { ...l, ...fields } : l)));
    };

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            {/* Section heading */}
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
                <h3 className="truncate text-sm font-semibold text-slate-800">{sectionTitle}</h3>
                <div className="flex shrink-0 gap-1.5">
                    <button
                        onClick={() => { setShowAddForm(true); setShowAddAssignment(false); setNewTitle(''); }}
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                    >
                        + Lesson
                    </button>
                    <button
                        onClick={() => { setShowAddAssignment(true); setShowAddForm(false); setNewAssignmentTitle(''); setNewAssignmentDescription(''); }}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                        + Assignment
                    </button>
                </div>
            </div>

            {error && (
                <div className="shrink-0 border-b border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
                    {error}
                    <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
                </div>
            )}

            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-5">
                {/* Lesson list */}
                <div className="min-h-0 space-y-2 overflow-y-auto border-r border-slate-100 p-3 lg:col-span-2">
                    {showAddForm && (
                        <form onSubmit={handleAddLesson} className="mb-2 space-y-1.5">
                            <input
                                autoFocus
                                type="text"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                placeholder="Lesson title"
                                required
                                className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex gap-1.5">
                                <button type="submit" className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700">Add</button>
                                <button type="button" onClick={() => setShowAddForm(false)} className="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">Cancel</button>
                            </div>
                        </form>
                    )}

                    {showAddAssignment && (
                        <form onSubmit={handleAddAssignment} className="mb-2 space-y-1.5">
                            <input
                                autoFocus
                                type="text"
                                value={newAssignmentTitle}
                                onChange={e => setNewAssignmentTitle(e.target.value)}
                                placeholder="Assignment title"
                                required
                                className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <textarea
                                value={newAssignmentDescription}
                                onChange={e => setNewAssignmentDescription(e.target.value)}
                                placeholder="Instructions (optional)"
                                rows={2}
                                className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex gap-1.5">
                                <button type="submit" className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700">Add</button>
                                <button type="button" onClick={() => setShowAddAssignment(false)} className="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">Cancel</button>
                            </div>
                        </form>
                    )}

                    {reordering && <p className="animate-pulse text-[10px] text-slate-400">Saving order…</p>}

                    {lessons.length === 0 && !showAddForm ? (
                        <div className="py-6 text-center text-xs text-slate-400">
                            No lessons yet. Add one above.
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={lessons.map(l => l.id)} strategy={verticalListSortingStrategy}>
                                {lessons.map(lesson => (
                                    <SortableLesson
                                        key={lesson.id}
                                        lesson={lesson}
                                        isSelected={selectedLesson?.id === lesson.id}
                                        onSelect={() => setSelectedLesson(lesson)}
                                        onDelete={() => handleDeleteLesson(lesson)}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}

                    {(assignments.length > 0 || showAddAssignment) && (
                        <div className="pt-3 space-y-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Assignments</p>
                            {assignments.map(assignment => (
                                <div
                                    key={assignment.id}
                                    className="rounded-xl border border-blue-100 bg-blue-50/60 p-3"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-slate-900 truncate">{assignment.title}</p>
                                            {assignment.description && (
                                                <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">{assignment.description}</p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteAssignment(assignment)}
                                            className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                                        >
                                            Del
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Lesson detail editor */}
                <div className="min-h-0 overflow-y-auto p-4 lg:col-span-3">
                    {selectedLesson ? (
                        <LessonDetailEditor
                            key={selectedLesson.id}
                            lesson={selectedLesson}
                            sections={sections}
                            currentSectionId={sectionId}
                            onUpdated={handleLessonUpdated}
                            onCloned={async () => {
                                await loadLessons();
                                onLessonsChange?.();
                            }}
                            onMoved={async () => {
                                setSelectedLesson(null);
                                await loadLessons();
                                onLessonsChange?.();
                            }}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center py-10">
                            <p className="text-sm text-slate-400">Select a lesson to edit its content</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ── Lesson Detail Editor ─────────────────────────────────────────── */

function LessonDetailEditor({
    lesson,
    sections,
    currentSectionId,
    onUpdated,
    onCloned,
    onMoved,
}: {
    lesson: Lesson;
    sections: Section[];
    currentSectionId: number;
    onUpdated: (fields?: Partial<Lesson>) => void;
    onCloned: () => void;
    onMoved: () => void;
}) {
    const [form, setForm] = useState({
        title: lesson.title,
        status: lesson.status,
        video_url: lesson.video_url || '',
        notes: lesson.notes || '',
        objectives: lesson.objectives || '',
        duration_minutes: lesson.duration_minutes != null ? String(lesson.duration_minutes) : '',
    });
    const [saving, setSaving] = useState(false);
    const [autosaveLabel, setAutosaveLabel] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [activeTab, setActiveTab] = useState<'meta' | 'blocks'>('meta');
    const [moveSectionId, setMoveSectionId] = useState('');

    const snapshot = JSON.stringify({
        title: form.title,
        video_url: form.video_url || '',
        notes: form.notes,
        objectives: form.objectives,
        duration_minutes: form.duration_minutes,
    });
    const lastSavedRef = useRef(snapshot);

    const buildPayload = () => ({
        title: form.title,
        status: form.status,
        video_url: form.video_url || null,
        notes: form.notes,
        objectives: form.objectives,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : null,
    });

    // Debounced autosave — only when the form actually differs from last saved.
    useEffect(() => {
        if (snapshot === lastSavedRef.current) return;
        if (!form.title.trim()) return;
        setAutosaveLabel('Saving…');
        const t = setTimeout(async () => {
            const payload = buildPayload();
            const res = await apiClient.patchLesson(lesson.id, payload);
            if (res.success) {
                lastSavedRef.current = snapshot;
                setAutosaveLabel('Saved');
                setTimeout(() => setAutosaveLabel(null), 1500);
                onUpdated({
                    id: lesson.id,
                    title: payload.title,
                    video_url: payload.video_url,
                    notes: payload.notes,
                    objectives: payload.objectives,
                    duration_minutes: payload.duration_minutes,
                });
            } else {
                setAutosaveLabel('Save failed');
            }
        }, 800);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot, form.title, lesson.id]);

    const save = async () => {
        if (!form.title.trim()) { setError('Title is required'); return; }
        setSaving(true);
        setError(null);
        const res = await apiClient.patchLesson(lesson.id, buildPayload());
        setSaving(false);
        if (res.success) {
            lastSavedRef.current = snapshot;
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
            onUpdated({ id: lesson.id, ...buildPayload() });
        } else {
            setError(res.error || 'Failed to save lesson');
        }
    };

    const handlePublishToggle = async () => {
        setSaving(true);
        const res = lesson.status === 'published'
            ? await apiClient.unpublishLesson(lesson.id)
            : await apiClient.publishLesson(lesson.id);
        setSaving(false);
        if (res.success) {
            const nextStatus = res.data?.status || (lesson.status === 'published' ? 'draft' : 'published');
            setForm(p => ({ ...p, status: nextStatus }));
            onUpdated({ id: lesson.id, status: nextStatus });
        } else {
            setError('Failed to update status');
        }
    };

    return (
        <div className="space-y-4">
            {/* Inner tab bar */}
            <div className="flex gap-4 border-b border-slate-100">
                {(['meta', 'blocks'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
                            activeTab === tab
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        {tab === 'meta' ? 'Lesson Info' : 'Content Blocks'}
                    </button>
                ))}
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                    {error}
                    <button className="ml-2 underline" onClick={() => setError(null)}>×</button>
                </div>
            )}

            {activeTab === 'meta' && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
                        <input
                            type="text"
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Duration (min)</label>
                            <input
                                type="number"
                                min="0"
                                value={form.duration_minutes}
                                onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))}
                                placeholder="e.g. 10"
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                            <div className="flex items-center gap-2 pt-1">
                                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[form.status as LessonStatus]}`}>
                                    {form.status}
                                </span>
                                <button
                                    onClick={handlePublishToggle}
                                    disabled={saving}
                                    className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                                >
                                    {form.status === 'published' ? 'Unpublish' : 'Publish'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Video URL</label>
                        <input
                            type="url"
                            value={form.video_url}
                            onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))}
                            placeholder="https://www.youtube.com/watch?v=…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Learning Objectives</label>
                        <textarea
                            value={form.objectives}
                            onChange={e => setForm(p => ({ ...p, objectives: e.target.value }))}
                            rows={2}
                            placeholder="What will students learn?"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                        <textarea
                            value={form.notes}
                            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                            rows={4}
                            placeholder="Lesson notes or supplementary text…"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                            onClick={save}
                            disabled={saving}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            {saving ? 'Saving…' : 'Save Lesson'}
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                const res = await apiClient.cloneLesson(lesson.id);
                                if (res.success) onCloned();
                                else setError(res.error || 'Clone failed');
                            }}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Clone lesson
                        </button>
                        {(success || autosaveLabel) && (
                            <span className="text-xs text-emerald-600 font-medium">
                                {autosaveLabel || '✓ Saved'}
                            </span>
                        )}
                    </div>

                    {sections.filter(s => s.id !== currentSectionId).length > 0 && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-2">
                            <label className="block text-xs font-medium text-slate-600">Move to section</label>
                            <div className="flex gap-2">
                                <select
                                    value={moveSectionId}
                                    onChange={e => setMoveSectionId(e.target.value)}
                                    className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                                >
                                    <option value="">Select section…</option>
                                    {sections.filter(s => s.id !== currentSectionId).map(s => (
                                        <option key={s.id} value={s.id}>{s.title}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    disabled={!moveSectionId}
                                    onClick={async () => {
                                        const res = await apiClient.moveLesson(lesson.id, parseInt(moveSectionId, 10));
                                        if (res.success) onMoved();
                                        else setError(res.error || 'Move failed');
                                    }}
                                    className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                                >
                                    Move
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'blocks' && (
                <LessonBlockEditor lessonId={lesson.id} />
            )}
        </div>
    );
}
